import React, { useEffect, useState, useRef } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";
import { Search, X, Radar, Target, Network } from "lucide-react";

export default function SearchModal({ open, onClose, onNavigate }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQ(""); setRes(null);
    }
  }, [open]);

  useEffect(() => {
    if (!q || q.length < 2) { setRes(null); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.search(q).then(setRes).finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => { onClose(); onNavigate(path); };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl bg-bm-surface border-white/10 p-0 rounded-none">
        <DialogTitle className="sr-only">Search Intelligence</DialogTitle>
        <DialogDescription className="sr-only">Natural language search across signals, opportunities, and insights.</DialogDescription>
        <div className="border-b border-white/10 px-4 py-3 flex items-center gap-3">
          <Search className="w-4 h-4 text-bm-amber" />
          <span className="font-mono text-xs text-bm-amber">{">"}</span>
          <input
            ref={inputRef}
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ask in natural language — e.g. korean skincare trends likely to work in india"
            className="flex-1 bg-transparent outline-none font-mono text-sm placeholder:text-bm-dim text-white"
          />
          <button onClick={onClose} className="text-bm-dim hover:text-white" data-testid="search-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-4">
          {!q && (
            <div className="text-bm-muted text-sm space-y-2 font-mono">
              <div className="label-mono text-bm-dim mb-2">EXAMPLE QUERIES</div>
              {[
                "humid-weather beauty trends gen z women",
                "korean skincare trends likely to work in india",
                "beauty tools accelerating in china",
                "wellness products scaling in north america",
              ].map(ex => (
                <button key={ex} data-testid={`search-example-${ex.slice(0, 10)}`} onClick={() => setQ(ex)} className="block text-left hover:text-bm-cyan transition-colors">
                  {">"} {ex}
                </button>
              ))}
            </div>
          )}
          {loading && (
            <div className="font-mono text-xs text-bm-cyan scanline py-4 px-2">SCANNING INTELLIGENCE CORPUS...</div>
          )}
          {res && (
            <div className="space-y-4">
              <div className="font-mono text-xs text-bm-amber" data-testid="search-interpretation">{res.interpretation}</div>

              {res.opportunities?.length > 0 && (
                <div>
                  <div className="label-mono text-bm-cyan mb-2 flex items-center gap-2"><Target className="w-3 h-3" />OPPORTUNITIES</div>
                  <div className="space-y-1">
                    {res.opportunities.map(o => (
                      <button key={o.id} onClick={() => go(`/opportunities/${o.id}`)} data-testid={`search-result-opp-${o.id}`}
                        className="w-full text-left p-2 hover:bg-white/[0.04] border border-transparent hover:border-white/10 flex items-center justify-between">
                        <div>
                          <div className="text-sm text-white">{o.product_name}</div>
                          <div className="text-xs text-bm-muted">{o.category} • {o.origin_geography}</div>
                        </div>
                        <span className="font-mono text-xs text-bm-green">{o.scores.overall}%</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {res.signals?.length > 0 && (
                <div>
                  <div className="label-mono text-bm-cyan mb-2 flex items-center gap-2"><Radar className="w-3 h-3" />SIGNALS</div>
                  <div className="space-y-1">
                    {res.signals.slice(0, 8).map(s => (
                      <button key={s.id} onClick={() => go("/signals")} data-testid={`search-result-signal-${s.id}`}
                        className="w-full text-left p-2 hover:bg-white/[0.04] border border-transparent hover:border-white/10">
                        <div className="text-sm text-white">{s.title}</div>
                        <div className="text-xs text-bm-muted">{s.source_platform} • {s.geography} • +{s.velocity}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {res.insights?.length > 0 && (
                <div>
                  <div className="label-mono text-bm-cyan mb-2 flex items-center gap-2"><Network className="w-3 h-3" />INSIGHTS</div>
                  <div className="space-y-1">
                    {res.insights.map(i => (
                      <button key={i.id} onClick={() => go("/insights")} data-testid={`search-result-insight-${i.id}`}
                        className="w-full text-left p-2 hover:bg-white/[0.04] border border-transparent hover:border-white/10">
                        <div className="text-sm text-white">{i.name}</div>
                        <div className="text-xs text-bm-muted">{i.layer} • {i.region}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
